import { NotificationScopes } from '../AppConstants'
import AppDispatcher from '../dispatcher'
import NotificationActionCreators from './NotificationActionCreators'
import { ActionTypes } from '../state/ActionTypes'
import type { ComposerProfile } from '../stores/types'
import WebAPIUtils from '../utils/WebAPIUtils'
const AppActionCreators = {
  clearComposerInlineErrors: (id: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_CLEAR_INLINE_ERRORS,
      id,
    })
  },

  createNewSubprofile: async (
    profileId: string,
    name: string,
  ): Promise<void> => {
    const data = { name }

    try {
      const newSubprofile = await WebAPIUtils.createNewSubprofile(
        profileId,
        data,
      )
      AppDispatcher.handleViewAction({
        actionType: ActionTypes.COMPOSER_CREATE_NEW_SUBPROFILE,
        profileId: newSubprofile.profileId,
        subprofileId: newSubprofile.id,
        subprofileServiceId: newSubprofile.serviceId,
        avatar: newSubprofile.avatar,
        name: newSubprofile.name,
        isShared: newSubprofile.isShared,
      })
    } catch (error) {
      AppDispatcher.handleViewAction({
        actionType: ActionTypes.COMPOSER_CREATE_NEW_SUBPROFILE_FAILED,
        profileId,
      })

      NotificationActionCreators.queueError({
        scope: NotificationScopes.BOARD_CREATION,
        data: { profileId },
        message:
          error instanceof Error ? error.message : 'Failed to create board',
      })
    }
  },

  refreshSubprofileData: async (profileId: string): Promise<void> => {
    const response = await WebAPIUtils.fetchProfileSubprofiles(profileId)
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.APP_REFRESH_SUBPROFILE_DATA,
      profileId,
      subprofileData: response.subprofiles,
    })
  },

  createSubprofilePending: (profileId: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_CREATE_NEW_SUBPROFILE_PENDING,
      profileId,
    })
  },

  setThumbnailLoading: (isLoading: boolean): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.APP_SET_THUMBNAIL_LOADING,
      isLoading,
    })
  },

  createProfiles: (profiles: ComposerProfile[]): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_CREATE_PROFILES,
      profiles,
    })
  },

  unselectSubprofile: (profileId: string, subprofileId: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.APP_UNSELECT_SUBPROFILE,
      profileId,
      subprofileId,
    })
  },

  selectSubprofile: (profileId: string, subprofileId: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.APP_SELECT_SUBPROFILE,
      profileId,
      subprofileId,
    })
  },

  selectProfile: (id: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_SELECT_PROFILE,
      id,
    })
  },

  unselectProfile: (id: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UNSELECT_PROFILE,
      id,
    })
  },

  selectProfiles: (
    ids: string[],
    markAppAsLoadedWhenDone = false,
    originatedFromGroupSelection = false,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_SELECT_PROFILES,
      ids,
      markAppAsLoadedWhenDone,
      originatedFromGroupSelection,
    })
  },

  updateShortenerForProfile: (profileId: string, domain: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.UPDATE_SHORTENER_FOR_PROFILE,
      profileId,
      domain,
    })
  },

  unselectProfiles: (ids: string[]): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UNSELECT_PROFILES,
      ids,
    })
  },

  queueProfileSubprofilesDropdownsIdsToExpand: (ids: string[]): void => {
    AppDispatcher.handleViewAction({
      actionType:
        ActionTypes.COMPOSER_QUEUE_PROFILES_SUBPROFILES_DROPDOWNS_TO_EXPAND,
      ids,
    })
  },

  emptyProfileSubprofilesDropdownsIdsToExpand: (): void => {
    AppDispatcher.handleViewAction({
      actionType:
        ActionTypes.COMPOSER_EMPTY_PROFILES_SUBPROFILES_DROPDOWNS_TO_EXPAND,
    })
  },

  selectProfilesOnBehalfOfUser: (
    ids: string[],
    markAppAsLoadedWhenDone?: boolean,
    originatedFromGroupSelection?: boolean,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_SELECT_PROFILES_ON_BEHALF_OF_USER,
      ids,
      markAppAsLoadedWhenDone,
      originatedFromGroupSelection,
    })
  },

  collapseProfileSubprofileDropdown: (id: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_COLLAPSE_PROFILE_SUBPROFILE_DROPDOWN,
      id,
    })
  },

  expandProfileSubprofileDropdown: (id: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_EXPAND_PROFILE_SUBPROFILE_DROPDOWN,
      id,
    })
  },

  profileDropdownHidden: (): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.PROFILE_DROPDOWN_HIDDEN,
    })
  },

  toggleAllProfiles: (): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.APP_TOGGLE_ALL_PROFILES,
    })
  },

  updateOmniboxState: (isEnabled: boolean | null): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.APP_UPDATE_OMNIBOX_STATE,
      isEnabled,
    })
  },

  updatePartiallySavedDraftsProfilesIds: (
    partiallySavedDrafts: Array<{ draftId: string; profilesIds: string[] }>,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.APP_UPDATE_PARTIALLY_SAVED_DRAFTS_PROFILES_IDS,
      partiallySavedDrafts,
    })
  },

  markAppAsLoaded: (): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.APP_LOADED,
    })
  },

  resetSelectedProfiles: (profilesData: ComposerProfile[]): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.RESET_PROFILES_DATA,
      profilesData,
    })
  },

  updateSelectedProfiles: (
    idsToSelect: string[],
    idsToUnselect: string[],
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.UPDATE_SELECTED_PROFILES,
      idsToSelect,
      idsToUnselect,
    })
  },

  getFacebookDomainOwnershipForProfile: (
    profileId: string,
    url: string,
  ): Promise<void> => {
    return WebAPIUtils.getFacebookDomainOwnershipForProfile(
      profileId,
      url,
    ).then((isOwner) => {
      AppDispatcher.handleApiAction({
        actionType: ActionTypes.APP_RECEIVE_FACEBOOK_DOMAIN_OWNERSHIP_DATA,
        profileId,
        url,
        isOwner,
      })
      return undefined
    })
  },

  refreshFacebookDomainOwnershipData: (): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.APP_REFRESH_FACEBOOK_DOMAIN_OWNERSHIP_DATA,
    })
  },
}

export default AppActionCreators
